/**
 * Linha "Focos no período exibido" do cartão do município (clique no mapa com
 * a camada de municípios ligada, ou no contorno do município).
 *
 * Os focos do município são separados UMA vez (`indexMunicipioFires`, refeito
 * só se o conjunto de focos mudar); a cada quadro do loop só se conta a janela
 * por busca binária, e o componente só renderiza quando os números mudam.
 */
import { Flame } from 'lucide-react';
import { useMemo } from 'react';
import type Geometry from 'ol/geom/Geometry';
import { useShallow } from 'zustand/react/shallow';
import { formatInt } from '../lib/format';
import { useTimeStore } from '../state/time';
import { countMunicipioWindow, indexMunicipioFires, type MunicipioShape } from './countInMunicipio';
import { useFireStore } from './store';
import { displayedSnapshot, loopPeriod, windowFor } from './timeFilter';
import type { DisplayMode } from './types';

const MODE_LABEL: Record<DisplayMode, string> = {
  frame: 'Focos no quadro exibido',
  accum: 'Focos até o quadro exibido',
  all: 'Focos no período exibido',
};

function shapeOf(geometry: Geometry | null | undefined): MunicipioShape | null {
  if (!geometry) return null;
  const coord = [0, 0];
  return {
    extent: geometry.getExtent(),
    contains: (x, y) => {
      coord[0] = x;
      coord[1] = y;
      return geometry.intersectsCoordinate(coord);
    },
  };
}

export function MunicipioFireCount(props: { cdMun: string | null; geometry: Geometry | null | undefined }) {
  const { cdMun, geometry } = props;
  const dataset = useFireStore((s) => s.dataset);
  const mode = useFireStore((s) => s.mode);
  const sources = useFireStore((s) => s.sources);
  const loading = useFireStore((s) => s.status.goes10min.state === 'loading' || s.status.diario.state === 'loading');
  const off = !sources.goes10min && !sources.diario;

  const shape = useMemo(() => shapeOf(geometry), [geometry]);
  const mf = useMemo(
    () => (off ? null : indexMunicipioFires(dataset, cdMun, shape)),
    [off, dataset, cdMun, shape],
  );

  // Só números: o cartão re-renderiza quando a contagem muda, não a cada quadro.
  const c = useTimeStore(
    useShallow((s) => {
      if (!mf) return null;
      const snap = displayedSnapshot(s);
      const w = windowFor(mode, snap);
      const period = loopPeriod(snap.frames, snap.range);
      if (!w || !period) return null;
      const shown = countMunicipioWindow(mf, w.from, w.to);
      return {
        total: shown.total,
        goes: shown.goes,
        others: shown.others,
        period: mode === 'all' ? shown.total : countMunicipioWindow(mf, period.from, period.to).total,
      };
    }),
  );

  let body;
  if (off) {
    body = <span className="text-ink-400">Focos de queimada desligados (aba Queimadas).</span>;
  } else if (!c || (loading && dataset.n === 0)) {
    body = <span className="text-ink-400">{loading ? 'Carregando focos…' : 'Focos ainda não carregados.'}</span>;
  } else {
    body = (
      <>
        <div>
          <span className="text-ink-300">{MODE_LABEL[mode]}:</span>{' '}
          <strong className="text-sm font-semibold text-white tabular-nums">{formatInt(c.total)}</strong>
        </div>
        {c.total > 0 && (
          <div className="text-ink-300 tabular-nums">
            {sources.diario ? (
              <>
                GOES-19: {formatInt(c.goes)} · outros satélites: {formatInt(c.others)}
              </>
            ) : (
              <>só GOES-19 (o arquivo diário, com os outros satélites, está desligado)</>
            )}
          </div>
        )}
        {mode !== 'all' && (
          <div className="text-ink-400 tabular-nums">No período todo do loop: {formatInt(c.period)}</div>
        )}
        {loading && <div className="text-ink-400">Atualizando focos…</div>}
      </>
    );
  }

  return (
    <div className="mt-2 flex gap-1.5 border-t border-ink-700/70 pt-1.5 text-xs" data-testid="municipio-fire-count">
      <Flame className="mt-0.5 size-3.5 shrink-0 text-fogo-400" aria-hidden />
      <div className="min-w-0 flex-1 space-y-0.5">{body}</div>
    </div>
  );
}
